/**
 * Per-account returns, as the Accounts page and the sidebar's Accounts
 * section both compute them: closed-trade profit of every strategy in the
 * account since a "Returns since" date, divided by the account's value at that
 * date.
 */

import { apiGet } from "@/lib/api";
import { accountBroker } from "@/lib/account-name";
import { $n } from "@/lib/format";
import { analysisStartFromRows } from "@/lib/strategy-config-keys";
import { tradeKind, type AnalysisSummary, type AnalysisTrade } from "@/app/strategies/components/analysis-pane";

/** GET /accounts/returns-window — earliest "Returns since" date per account. */
export interface ReturnsWindow {
  accountHash: string;
  since: string;
  sinceSource: "strategy" | "trades";
}

export type SinceSource = "account" | "strategy" | "trades";

interface StatementAsOf {
  cash: number;
  totalAccountValue: number | null;
  positions: { kind: string; qty: number; marketValue: number | null; costBasis?: number | null }[];
}

/**
 * The strategy analysis endpoints that report closed-trade profit, with the
 * ledger row codes their trades carry (0DTE rows use the Z family).
 */
export const ANALYSIS_SOURCES = [
  { id: "vs", endpoint: "/vs/analysis", put: "CPSC", call: "CCSC" },
  { id: "vs0", endpoint: "/vs0/analysis", put: "ZPSC", call: "ZCSC" },
  { id: "vse", endpoint: "/vse/analysis", put: "CPSC", call: "CCSC" },
] as const;

/**
 * The account-level "Returns since" date lives in account_strategy_settings
 * under this pseudo-strategy, next to each real strategy's own date.
 */
export const ACCOUNT_SETTINGS_STRATEGY = "account";

/** Fired on `window` when an account's "Returns since" date is saved. */
export const ACCOUNT_RETURNS_CHANGED = "account-returns-changed";

/** All strategies' analyses for one account merged into one. */
export interface Combined extends AnalysisSummary {
  latestClose: string;
  /** Realized profit of trades closed today (ET). */
  todayPnl: number;
}

/** Account value as of a statement date: cash + equity MV − net open option premium. */
function statementAccountValue(st: StatementAsOf): number {
  if (st.totalAccountValue != null) return st.totalAccountValue;
  const equityMv = st.positions
    .filter((p) => p.kind === "equity")
    .reduce((sum, p) => sum + (p.marketValue ?? 0), 0);
  const shortPrem = st.positions
    .filter((p) => p.kind === "option" && p.qty < -0.01)
    .reduce((sum, p) => sum + (p.costBasis ?? 0), 0);
  const longPrem = st.positions
    .filter((p) => p.kind === "option" && p.qty > 0.01)
    .reduce((sum, p) => sum + (p.costBasis ?? 0), 0);
  return st.cash + equityMv - (shortPrem - longPrem);
}

export async function fetchStartValue(hash: string, asOf: string): Promise<number> {
  const st = await apiGet<StatementAsOf>(
    `/trades/statement-as-of?accountHash=${encodeURIComponent(hash)}&asOf=${encodeURIComponent(asOf)}`,
  );
  return statementAccountValue(st);
}

export const todayET = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

/**
 * Peak concurrent capital at risk across the merged trades — the high-water
 * mark of simultaneously open spreads, as each strategy's endpoint computes it
 * for its own trades. Closes are processed before opens at the same instant so
 * a same-moment rotate does not inflate the peak.
 */
export function peakConcurrentRisk(trades: AnalysisTrade[]): number {
  const events: { t: number; close: boolean; risk: number }[] = [];
  for (const tr of trades) {
    const open = new Date(tr.openedDate + "T00:00:00").getTime();
    let close = tr.closedAt ? new Date(tr.closedAt).getTime() : NaN;
    if (!Number.isFinite(close)) close = new Date(tr.closedDate + "T23:59:59").getTime();
    events.push({ t: open, close: false, risk: tr.maxLoss });
    events.push({ t: close, close: true, risk: tr.maxLoss });
  }
  events.sort((a, b) => a.t - b.t || (a.close === b.close ? 0 : a.close ? -1 : 1));
  let running = 0;
  let peak = 0;
  for (const e of events) {
    running += e.close ? -e.risk : e.risk;
    if (running > peak) peak = running;
  }
  return Math.max(0, peak);
}

/** GET /accounts/closed-trades — every closed option leg and stock round trip. */
export interface ClosedTrade {
  kind: "option" | "equity";
  symbol: string;
  underlying: string;
  putCall: string;
  strike: number;
  expiration: string;
  /** Signed peak size; negative = short. */
  qty: number;
  entryPrice: number;
  pnl: number;
  openedAt: string;
  closedAt: string;
}

const etDate = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const stripIndex = (u: string) => u.replace(/^\$/, "").toUpperCase();

/**
 * When a closed leg actually died. The broker books expiration and assignment
 * overnight, so those fills land on the day after expiry; the position ended
 * at that expiry's close, which is when the strategy endpoints close it too.
 */
function legClosedAt(c: ClosedTrade): string {
  if (c.kind !== "option" || !c.expiration || etDate(c.closedAt) <= c.expiration) return c.closedAt;
  for (const off of ["-04:00", "-05:00"]) {
    const d = new Date(`${c.expiration}T16:00:00${off}`);
    if (d.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", hour12: false }) === "16") {
      return d.toISOString();
    }
  }
  return c.closedAt;
}

/**
 * A closed option leg or stock round trip in the same row shape as a spread,
 * so the Analysis pane, the profit tables and the risk sweep treat them alike.
 * Capital at risk: what was paid for a long; for a short option the strike
 * value less the credit, as if cash-secured. RoR is annualized on that capital
 * the way the spread endpoints do it.
 */
function closedTradeRow(t: ClosedTrade, id: number): AnalysisTrade {
  const short = t.qty < 0;
  const q = Math.abs(t.qty);
  const mult = t.kind === "equity" ? 1 : 100;
  const openedDate = etDate(t.openedAt);
  const closedDate = etDate(t.closedAt);
  const daysHeld = Math.max(0, daysBetween(openedDate, closedDate));
  const daysFull = t.kind === "option" && t.expiration ? Math.max(0, daysBetween(openedDate, t.expiration)) : daysHeld;
  const entryTotal = t.entryPrice * mult * q;
  // pnl = (exit − entry) for longs, (entry − exit) for shorts, per unit × size.
  const exitPx = q > 0 ? (short ? t.entryPrice - t.pnl / (mult * q) : t.entryPrice + t.pnl / (mult * q)) : 0;
  const capital = Math.max(1e-9, short && t.kind === "option" ? t.strike * mult * q - entryTotal : entryTotal);
  const ror = (t.pnl / capital) * (365 / Math.max(1, daysHeld));
  const pc = t.putCall === "PUT" ? "P" : "C";
  const strategyType = t.kind === "equity"
    ? (short ? "short_stock" : "long_stock")
    : `${short ? "short" : "long"}_${t.putCall === "PUT" ? "put" : "call"}`;
  return {
    id,
    underlying: t.underlying || t.symbol,
    strategyType,
    openedDate, closedDate, closedAt: t.closedAt,
    expDate: t.expiration,
    dte: t.expiration ? Math.max(0, daysBetween(openedDate, t.expiration)) : 0,
    shortStrike: t.strike, longStrike: t.strike,
    qty: q,
    entrySpot: 0, expSpot: 0,
    entryCredit: short ? entryTotal : -entryTotal,
    entryCreditPerShare: t.entryPrice,
    exitCost: exitPx * mult * q,
    maxLoss: capital,
    probItm: null, epItm: null, exitProbItm: null, exitEpItm: null, finishedItm: false,
    earlyPnl: t.pnl, holdPnl: t.pnl,
    daysHeld, daysFull,
    rorEarly: ror, rorHold: ror,
    code: t.kind === "equity" ? (short ? "SEC" : "LEC") : `${short ? "S" : "L"}${pc}C`,
    source: "pos",
    kind: t.kind,
    symbol: t.symbol,
    multiplier: mult,
  };
}

/** A spread row built from two closed legs that no strategy endpoint reported. */
function spreadRow(short: ClosedTrade, long: ClosedTrade, id: number): AnalysisTrade {
  const q = Math.abs(short.qty);
  const openedDate = etDate(short.openedAt);
  const closedAt = short.closedAt > long.closedAt ? short.closedAt : long.closedAt;
  const closedDate = etDate(closedAt);
  const isPut = short.putCall === "PUT";
  const strategyType = isPut && short.strike > long.strike ? "bull_put"
    : !isPut && short.strike < long.strike ? "bear_call" : "spread";
  const creditPerShare = short.entryPrice - long.entryPrice;
  const entryCredit = creditPerShare * 100 * q;
  const pnl = short.pnl + long.pnl;
  const width = Math.abs(short.strike - long.strike);
  const maxLoss = Math.max(1e-9, (width - creditPerShare) * 100 * q);
  const daysHeld = Math.max(0, daysBetween(openedDate, closedDate));
  const daysFull = Math.max(0, daysBetween(openedDate, short.expiration));
  const ror = (pnl / maxLoss) * (365 / Math.max(1, daysHeld));
  return {
    id,
    underlying: short.underlying,
    strategyType,
    openedDate, closedDate, closedAt,
    expDate: short.expiration,
    dte: daysFull,
    shortStrike: short.strike, longStrike: long.strike,
    qty: q,
    entrySpot: 0, expSpot: 0,
    entryCredit, entryCreditPerShare: creditPerShare,
    exitCost: entryCredit - pnl,
    maxLoss,
    probItm: null, epItm: null, exitProbItm: null, exitEpItm: null, finishedItm: false,
    earlyPnl: pnl, holdPnl: pnl,
    daysHeld, daysFull,
    rorEarly: ror, rorHold: ror,
    code: isPut ? "CPSC" : "CCSC",
    source: "legs",
    kind: "spread",
  };
}

/**
 * Merge every closed position in the account opened on or after `since` into
 * one trade list. Spreads a strategy endpoint reports are taken from it
 * unchanged — the same rows, dates and money the strategy pages show, one row
 * per close (a partial buy-back is its own row). Those spreads' legs are then
 * taken off the closed legs; whatever legs remain are paired into spreads
 * (one short and one long of equal size, same contract family and entry day)
 * or shown as single option rows, and stock round trips are rows of their own.
 */
export function combineClosedTrades(parts: AnalysisSummary[], closed: ClosedTrade[], since: string): Combined {
  const today = todayET();
  const out: Combined = {
    nTrades: 0, nWins: 0, totalPnl: 0, totalCredit: 0, totalCapitalAtRisk: 0,
    avgDaysEarly: 0, avgDaysFull: 0, skipped: [], trades: [], latestClose: "", todayPnl: 0,
  };

  // Option legs still to account for: remaining size and profit.
  const legs = closed
    .filter((c) => c.kind === "option")
    .map((c) => ({ ...c, closedAt: legClosedAt(c), rem: Math.abs(c.qty) }))
    .sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  // Take `qty` contracts of one strategy spread's leg off the closed legs,
  // same-entry-day legs first (one leg row can hold several entries).
  const consume = (t: AnalysisTrade, strike: number, sign: number) => {
    const pc = t.strategyType.includes("put") ? "PUT" : "CALL";
    const undl = stripIndex(t.underlying);
    const cands = legs.filter((l) => l.rem > 1e-9 && Math.sign(l.qty) === sign && l.putCall === pc
      && l.strike === strike && l.expiration === t.expDate && stripIndex(l.underlying) === undl);
    cands.sort((a, b) => Number(etDate(b.openedAt) === t.openedDate) - Number(etDate(a.openedAt) === t.openedDate));
    let need = t.qty;
    for (const l of cands) {
      if (need <= 1e-9) break;
      const take = Math.min(need, l.rem);
      l.rem -= take;
      need -= take;
    }
  };

  parts.forEach((a, i) => {
    const src = ANALYSIS_SOURCES[i];
    out.skipped.push(...a.skipped);
    for (const t of a.trades) {
      const isPut = t.strategyType.includes("put");
      out.trades.push({ ...t, code: t.code ?? (isPut ? src.put : src.call), source: src.id, kind: "spread" });
      consume(t, t.shortStrike, -1);
      consume(t, t.longStrike, 1);
    }
  });

  // What is left of each leg, profit pro rata to the size left, opened in the window.
  const rest: ClosedTrade[] = legs
    .filter((l) => l.rem > 1e-9 && etDate(l.openedAt) >= since)
    .map(({ rem, ...l }) => ({ ...l, qty: Math.sign(l.qty) * rem, pnl: l.pnl * rem / Math.abs(l.qty) }));

  // Pair legs: same underlying, expiry, side, entry day and size. The exit
  // day is deliberately not part of the key — a spread's legs can settle on
  // different days (the short assigned after the long expires), and an
  // unpaired short index leg would otherwise read as a naked put with the
  // whole strike at risk.
  const groups = new Map<string, ClosedTrade[]>();
  for (const c of rest) {
    const k = `${stripIndex(c.underlying)}|${c.expiration}|${c.putCall}|${etDate(c.openedAt)}|${Math.abs(c.qty)}`;
    const g = groups.get(k);
    if (g) g.push(c); else groups.set(k, [c]);
  }
  let nextId = 1;
  const leftovers: ClosedTrade[] = [];
  for (const g of groups.values()) {
    const shorts = g.filter((c) => c.qty < 0).sort((a, b) => b.strike - a.strike);
    const longs = g.filter((c) => c.qty > 0).sort((a, b) => b.strike - a.strike);
    const n = Math.min(shorts.length, longs.length);
    for (let i = 0; i < n; i += 1) out.trades.push(spreadRow(shorts[i], longs[i], nextId++));
    leftovers.push(...shorts.slice(n), ...longs.slice(n));
  }
  for (const c of leftovers) out.trades.push(closedTradeRow(c, nextId++));
  for (const c of closed) {
    if (c.kind === "equity" && etDate(c.openedAt) >= since) out.trades.push(closedTradeRow(c, nextId++));
  }

  out.trades.sort((a, b) => (a.closedAt || a.closedDate).localeCompare(b.closedAt || b.closedDate));
  out.nTrades = out.trades.length;
  for (const t of out.trades) {
    if (t.earlyPnl > 0) out.nWins += 1;
    out.totalPnl += t.earlyPnl;
    if (tradeKind(t) === "spread") out.totalCredit += t.entryCredit;
    out.avgDaysEarly += t.daysHeld;
    out.avgDaysFull += t.daysFull;
    if (t.closedDate > out.latestClose) out.latestClose = t.closedDate;
    if (t.closedDate === today) out.todayPnl += t.earlyPnl;
  }
  if (out.nTrades > 0) {
    out.avgDaysEarly /= out.nTrades;
    out.avgDaysFull /= out.nTrades;
  }
  out.totalCapitalAtRisk = peakConcurrentRisk(out.trades);
  return out;
}

/** Every closed trade in the account opened since `since` — see `combineClosedTrades`. */
export async function fetchCombined(hash: string, since: string): Promise<Combined> {
  const params = new URLSearchParams({ account: hash, start: since });
  // The legs endpoint returns what closed AFTER its start day; ask from the
  // day before so a trade opened and closed on `since` itself is included.
  const legParams = new URLSearchParams({ account: hash, start: dayBefore(since) });
  const [parts, closed] = await Promise.all([
    Promise.all(ANALYSIS_SOURCES.map((src) => apiGet<AnalysisSummary>(`${src.endpoint}?${params.toString()}`))),
    apiGet<ClosedTrade[]>(`/accounts/closed-trades?${legParams.toString()}`),
  ]);
  return combineClosedTrades(parts, closed, since);
}

/** The account-level "Returns since" date saved on the Accounts page, "" when none. */
export async function fetchAccountStart(hash: string): Promise<string> {
  const rows = await apiGet<{ key: string; value: string }[]>(
    `/strategies/account-settings/${ACCOUNT_SETTINGS_STRATEGY}?account=${encodeURIComponent(hash)}`,
  );
  return analysisStartFromRows(rows);
}

/**
 * The app's own reconstruction of the account's value today — the same
 * statement-as-of figure the "Start value" uses, evaluated for today: cash from
 * every recorded trade (prices are net of fees), deposit, withdrawal, dividend
 * and interest, plus stock at the last close and open options at their premium.
 */
export function fetchBalance(hash: string): Promise<number> {
  return fetchStartValue(hash, todayET());
}

/** GET /accounts/cash-flows — one row per day money moved in (+) or out (−), oldest first. */
export interface CashFlow {
  date: string;
  amount: number;
}

export async function fetchCashFlows(hash: string, since: string): Promise<CashFlow[]> {
  const params = new URLSearchParams({ account: hash, start: since });
  return apiGet<CashFlow[]>(`/accounts/cash-flows?${params.toString()}`);
}

/** Calendar days between two YYYY-MM-DD dates. */
export function daysBetween(from: string, to: string): number {
  return Math.round((new Date(to + "T00:00:00").getTime() - new Date(from + "T00:00:00").getTime()) / 86400000);
}

const dayBefore = (ymd: string) => {
  const d = new Date(ymd + "T00:00:00");
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString("en-CA");
};

/** Compound a period return over `days` to one year. */
export function annualized(r: number, days: number): number | null {
  if (days < 1 || r <= -1) return null;
  return Math.pow(1 + r, 365 / days) - 1;
}

/** The account's time-weighted return over a window, plus how it was built. */
export interface AccountReturn {
  /** Chained return of the pieces between cash flows. */
  total: number | null;
  /** `total` compounded to one year over `days`. */
  annualized: number | null;
  /** Calendar days from the window start to today. */
  days: number;
  /** One piece per stretch with no money moving; the first starts at the window start. */
  pieces: { from: string; to: string; ret: number }[];
}

/**
 * Time-weighted return: the window is cut at every day money moved in or out.
 * Each piece is valued from the close before the flow (plus the flow, which
 * is taken to arrive before that day's trading) to the close before the next
 * flow, and the pieces are chained, so cash moving in or out never counts as
 * gain or loss. Values come from the same statement-as-of reconstruction as
 * Start value and Balance.
 */
export async function fetchAccountReturn(
  hash: string,
  since: string,
  startValue: number,
  balance: number,
): Promise<AccountReturn> {
  const today = todayET();
  const days = daysBetween(since, today);
  const flows = (await fetchCashFlows(hash, since)).filter((f) => f.date <= today);
  // Value at the close before each flow day.
  const before = await Promise.all(flows.map((f) => fetchStartValue(hash, dayBefore(f.date))));

  const pieces: AccountReturn["pieces"] = [];
  let open = startValue; // value the current piece started with, cash included
  let from = since;
  let ok = startValue > 0;
  flows.forEach((f, i) => {
    const endValue = before[i];
    if (open > 0) pieces.push({ from, to: dayBefore(f.date), ret: endValue / open - 1 });
    else ok = false;
    open = endValue + f.amount;
    from = f.date;
  });
  if (open > 0) pieces.push({ from, to: today, ret: balance / open - 1 });
  else ok = false;

  const total = ok ? pieces.reduce((acc, p) => acc * (1 + p.ret), 1) - 1 : null;
  return { total, annualized: total == null ? null : annualized(total, days), days, pieces };
}

/** GET /st/returns — a Schwab account's returns since a date, from the broker. */
export interface BrokerReturn {
  startValue: number;
  balance: number;
  netFlows: number;
  pnl: number;
  todayPnl: number;
  total: number | null;
  annualized: number | null;
  days: number;
  pieces: { from: string; to: string; ret: number }[];
  unpriced: string[];
}

/**
 * Returns straight from the broker: today's holdings rolled back through its
 * transactions and priced at each day's close. For accounts the app holds no
 * trade history for (managed accounts). Schwab only.
 */
export function fetchBrokerReturn(hash: string, since: string, excludeOptions = false): Promise<BrokerReturn> {
  const opt = excludeOptions ? "&excludeOptions=true" : "";
  return apiGet<BrokerReturn>(`/st/returns?account=${encodeURIComponent(hash)}&since=${since}${opt}`);
}

/** One holding's change since the Returns since date (GET /st/analysis). */
export interface StHolding {
  symbol: string;
  startQty: number;
  startPrice: number | null;
  qty: number;
  price: number | null;
  startValue: number;
  value: number;
  /** Cash put in by buys less cash taken out by sales in the window. */
  netBought: number;
  gain: number;
  dayGain: number;
}

export type StockAnalysis = Combined & {
  holdings: StHolding[];
  dividends: number | null;
  /** False when the broker reported no transactions at all for the window. */
  brokerHistory: boolean;
  /** No broker transactions: the app's imported trades were used; they run through this day. */
  ledgerThrough: string | null;
};

/**
 * GET /st/analysis — a Schwab account's non-option holdings since a date
 * (each one's gain) and its closed lots (first in, first out), from the
 * broker. When the broker reports no transactions (managed accounts), the
 * backend uses the trades imported into the app instead.
 */
export async function fetchStockAnalysis(hash: string, since: string): Promise<StockAnalysis> {
  const ledger = async () => {
    const legParams = new URLSearchParams({ account: hash, start: dayBefore(since) });
    const closed = await apiGet<ClosedTrade[]>(`/accounts/closed-trades?${legParams.toString()}`);
    return combineClosedTrades([], closed.filter((c) => c.kind === "equity"), since);
  };
  if (accountBroker({ hashValue: hash }) !== "schwab") {
    return { ...(await ledger()), holdings: [], dividends: null, brokerHistory: false, ledgerThrough: null };
  }
  const r = await apiGet<{
    trades: ClosedTrade[]; holdings: StHolding[]; dividends: number; skipped: string[];
    transactions: Record<string, number>; ledgerThrough: string | null;
  }>(`/st/analysis?account=${encodeURIComponent(hash)}&since=${since}`);
  const brokerHistory = Object.keys(r.transactions).length > 0;
  const c = combineClosedTrades([], r.trades, since);
  c.skipped.push(...r.skipped);
  return {
    ...c, holdings: r.holdings, dividends: brokerHistory ? r.dividends : null, brokerHistory,
    ledgerThrough: r.ledgerThrough,
  };
}

/** Total time-weighted return since `since` — the sidebar's and the mobile picker's number. */
export async function fetchTotalReturn(hash: string, since: string): Promise<number | null> {
  const [startValue, balance] = await Promise.all([fetchStartValue(hash, since), fetchBalance(hash)]);
  // No trade history in the app (start value 0): ask the broker instead.
  if (!(startValue > 0) && accountBroker({ hashValue: hash }) === "schwab") return (await fetchBrokerReturn(hash, since)).total;
  return (await fetchAccountReturn(hash, since, startValue, balance)).total;
}

/** One account's part in the all-accounts return. */
export interface AggregateMember {
  hash: string;
  since: string;
  startValue: number;
  balance: number;
}

/**
 * Time-weighted return of every account taken together as one pot. Each
 * account joins at the close of its own "Returns since" date, as if its start
 * value were deposited then; after that the window is cut at every deposit or
 * withdrawal in any account, exactly as `fetchAccountReturn` does for one.
 * Past values are kept in `cache` (key "hash|date") so a refresh only asks
 * again for today.
 */
export async function fetchAggregateReturn(
  members: AggregateMember[],
  cache: Map<string, number>,
): Promise<AccountReturn | null> {
  if (members.length === 0) return null;
  const today = todayET();
  const flowsByMember = await Promise.all(
    members.map((m) => fetchCashFlows(m.hash, m.since).then((fs) => fs.filter((f) => f.date <= today))),
  );
  // Cut points (closes): each account's start, and the close before each flow.
  const cuts = [...new Set([
    ...members.map((m) => m.since),
    ...flowsByMember.flatMap((fs) => fs.map((f) => dayBefore(f.date))),
  ])].sort();

  const valueAt = async (m: AggregateMember, d: string): Promise<number> => {
    if (d === m.since) return m.startValue;
    if (d >= today) return m.balance;
    const key = `${m.hash}|${d}`;
    const hit = cache.get(key);
    if (hit != null) return hit;
    const v = await fetchStartValue(m.hash, d);
    cache.set(key, v);
    return v;
  };
  // Value of every account already in the pot at each cut.
  const values = await Promise.all(cuts.map((d) =>
    Promise.all(members.map((m) => (m.since <= d ? valueAt(m, d) : Promise.resolve(0)))),
  ));

  const pieces: AccountReturn["pieces"] = [];
  let open = 0;
  let from = cuts[0];
  let ok = true;
  cuts.forEach((d, i) => {
    if (i > 0) {
      // Accounts that were already in before this cut.
      const end = members.reduce((sum, m, j) => sum + (m.since < d ? values[i][j] : 0), 0);
      if (open > 0) pieces.push({ from, to: d, ret: end / open - 1 });
      else ok = false;
    }
    const inflow = flowsByMember.reduce(
      (sum, fs) => sum + fs.filter((f) => dayBefore(f.date) === d).reduce((s, f) => s + f.amount, 0),
      0,
    );
    open = values[i].reduce((a, b) => a + b, 0) + inflow;
    from = d;
  });
  const balance = members.reduce((sum, m) => sum + m.balance, 0);
  if (open > 0) pieces.push({ from, to: today, ret: balance / open - 1 });
  else ok = false;

  const days = daysBetween(cuts[0], today);
  const total = ok ? pieces.reduce((acc, p) => acc * (1 + p.ret), 1) - 1 : null;
  return { total, annualized: total == null ? null : annualized(total, days), days, pieces };
}

export async function fetchReturnsWindows(): Promise<Map<string, ReturnsWindow>> {
  const windows = await apiGet<ReturnsWindow[]>("/accounts/returns-window");
  return new Map(windows.map((w) => [w.accountHash, w]));
}

/** The effective window start: the account-level date, else the earliest strategy date, else the earliest trade. */
export function resolveSince(
  accountStart: string,
  w: ReturnsWindow | undefined,
): { since: string | null; sinceSource: SinceSource | null } {
  if (accountStart) return { since: accountStart, sinceSource: "account" };
  return { since: w?.since ?? null, sinceSource: w?.sinceSource ?? null };
}

// Same number styling as the strategy pages' "Returns since" header.
export const pct1 = (x: number) => {
  const t = `${(Math.abs(x) * 100).toFixed(1)}%`;
  return x < 0 ? `(${t})` : t;
};
/** Signed one-decimal percent for an account's total return: +10.7% / -6.3%. */
export const signedPct1 = (x: number) => `${x > 0 ? "+" : x < 0 ? "-" : ""}${(Math.abs(x) * 100).toFixed(1)}%`;
export const money = (n: number) => {
  const t = "$" + $n(Math.abs(n));
  return n < 0 ? `(${t})` : t;
};
export const tone = (x: number) => (x >= 0 ? "text-green" : "text-red");
